import { Job, Entry, User, ProjectData, MediaAttachment } from '../types';

const RECENT_SLUGS_KEY = 'wtw_recent_slugs';
const LOCAL_STORAGE_PREFIX = 'wtw_local_';

// Helper for local mock fallback
function getLocalFallback(slug: string): ProjectData | null {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_PREFIX + slug);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    console.error(e);
  }
  return null;
}

function saveLocalFallback(slug: string, data: ProjectData) {
  try {
    localStorage.setItem(LOCAL_STORAGE_PREFIX + slug, JSON.stringify(data));
  } catch (e) {
    console.error(e);
  }
}

export const api = {
  // Recent Slugs
  getRecentSlugs(): Array<{ slug: string; title: string; category: string; lastVisited: number }> {
    try {
      const raw = localStorage.getItem(RECENT_SLUGS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  },

  addRecentSlug(slug: string, title: string, category: string) {
    try {
      const list = this.getRecentSlugs().filter(s => s.slug !== slug);
      list.unshift({ slug, title, category, lastVisited: Date.now() });
      localStorage.setItem(RECENT_SLUGS_KEY, JSON.stringify(list.slice(0, 15)));
    } catch (e) {
      console.error(e);
    }
  },

  // Project API
  async getProject(slug: string): Promise<ProjectData> {
    try {
      const res = await fetch(`/api/projects/${encodeURIComponent(slug)}`);
      if (res.ok) {
        const data = await res.json();
        saveLocalFallback(slug, data);
        this.addRecentSlug(slug, data.job.title, data.job.category);
        return data;
      }
      if (res.status === 403) {
        const data = await res.json();
        throw new Error(data.error || 'Private project. Login required.');
      }
      if (res.status === 404) {
        // Project not yet created on backend, check local
        const local = getLocalFallback(slug);
        if (local) return local;
        // Return blank skeleton for free user instant creation
        return {
          job: {
            id: 'job_' + Math.random().toString(36).slice(2, 10),
            slug,
            title: slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            description: 'Live timeline log for ' + slug,
            category: 'gardening',
            is_private: false,
            settings: {
              plantVariety: 'Heirloom Tomato',
              sproutDate: new Date().toISOString().split('T')[0],
              lightSchedule: '18/6',
              currentStage: 'Vegetative',
              targetYield: 500,
              yieldUnit: 'grams',
              customFields: [
                { id: 'f_temp', name: 'Temperature', type: 'number', unit: '°F' },
                { id: 'f_humidity', name: 'Humidity', type: 'number', unit: '%' },
                { id: 'f_light', name: 'Light Hours', type: 'number', unit: 'hrs' },
              ],
            },
            created_at: Math.floor(Date.now() / 1000),
            updated_at: Math.floor(Date.now() / 1000),
          },
          entries: [],
        };
      }
    } catch (err: any) {
      if (err.message && err.message.includes('Private')) {
        throw err;
      }
      // Offline fallback
      const local = getLocalFallback(slug);
      if (local) return local;
    }

    // Default mock project for instant preview
    const defaultData: ProjectData = {
      job: {
        id: 'job_' + Math.random().toString(36).slice(2, 10),
        slug,
        title: slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        description: 'Live work & plant log with calendar entries and notes.',
        category: 'gardening',
        is_private: false,
        settings: {
          plantVariety: 'Green Monstera & Peppers',
          sproutDate: new Date(Date.now() - 14 * 86400000).toISOString().split('T')[0],
          lightSchedule: '18/6',
          currentStage: 'Vegetative',
          targetYield: 600,
          yieldUnit: 'grams',
          customFields: [
            { id: 'f_temp', name: 'Temperature', type: 'number', unit: '°F' },
            { id: 'f_humidity', name: 'Humidity', type: 'number', unit: '%' },
            { id: 'f_light', name: 'Light Cycle', type: 'text', unit: 'hrs' },
          ],
        },
        created_at: Math.floor(Date.now() / 1000),
        updated_at: Math.floor(Date.now() / 1000),
      },
      entries: [
        {
          id: 'ent_demo_1',
          job_id: 'job_demo',
          title: 'Sprout Emerged & New Fan Leaves',
          content: 'Healthy vibrant green leaves showing great root vigor under full spectrum lighting.',
          entry_date: new Date(Date.now() - 3 * 86400000).toISOString(),
          category_type: 'milestone',
          custom_fields: { f_temp: 74, f_humidity: 62, f_light: '18/6' },
          attachments: [
            {
              id: 'med_1',
              filename: 'seedling_growth.jpg',
              content_type: 'image/jpeg',
              url: 'https://images.unsplash.com/photo-1592150621744-aca64f48394a?w=800&auto=format&fit=crop&q=80',
            },
          ],
          created_at: Math.floor(Date.now() / 1000) - 86400 * 3,
          updated_at: Math.floor(Date.now() / 1000) - 86400 * 3,
        },
        {
          id: 'ent_demo_2',
          job_id: 'job_demo',
          title: 'Nutrient Feeding & pH Balanced to 6.2',
          content: 'Added organic cal-mag and seaweed extract. Soil moisture optimal.',
          entry_date: new Date(Date.now() - 1 * 86400000).toISOString(),
          category_type: 'feeding',
          custom_fields: { f_temp: 75, f_humidity: 58, f_light: '18/6' },
          attachments: [],
          created_at: Math.floor(Date.now() / 1000) - 86400,
          updated_at: Math.floor(Date.now() / 1000) - 86400,
        },
      ],
    };
    saveLocalFallback(slug, defaultData);
    this.addRecentSlug(slug, defaultData.job.title, defaultData.job.category);
    return defaultData;
  },

  async saveProject(slug: string, jobData: Partial<Job>): Promise<void> {
    try {
      const res = await fetch(`/api/projects/${encodeURIComponent(slug)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(jobData),
      });
      if (res.ok) return;
    } catch (e) {
      console.warn('Backend unavailable, saving locally', e);
    }
    // Local save
    const current = getLocalFallback(slug);
    if (current) {
      current.job = { ...current.job, ...jobData, updated_at: Math.floor(Date.now() / 1000) };
      saveLocalFallback(slug, current);
      this.addRecentSlug(slug, current.job.title, current.job.category);
    }
  },

  async addEntry(entryData: Partial<Entry> & { job_id: string }): Promise<Entry> {
    try {
      const res = await fetch('/api/entries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entryData),
      });
      if (res.ok) {
        const json = await res.json();
        return json.entry;
      }
    } catch (e) {
      console.warn('Backend unavailable, creating local entry', e);
    }

    const newEntry: Entry = {
      id: 'ent_' + Math.random().toString(36).slice(2, 10),
      job_id: entryData.job_id,
      title: entryData.title || 'Log entry',
      content: entryData.content || '',
      entry_date: entryData.entry_date || new Date().toISOString(),
      category_type: entryData.category_type || 'log',
      custom_fields: entryData.custom_fields || {},
      attachments: entryData.attachments || [],
      created_at: Math.floor(Date.now() / 1000),
      updated_at: Math.floor(Date.now() / 1000),
    };
    return newEntry;
  },

  async updateEntry(id: string, entryData: Partial<Entry>): Promise<void> {
    try {
      await fetch(`/api/entries/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entryData),
      });
    } catch (e) {
      console.warn('Updated entry locally', e);
    }
  },

  async deleteEntry(id: string): Promise<void> {
    try {
      await fetch(`/api/entries/${id}`, { method: 'DELETE' });
    } catch (e) {
      console.warn('Deleted entry locally', e);
    }
  },

  async uploadMedia(jobId: string, file: File): Promise<MediaAttachment> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('job_id', jobId);

    try {
      const res = await fetch('/api/media/upload', {
        method: 'POST',
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        return data.media;
      }
    } catch (e) {
      console.warn('Using client blob URL fallback', e);
    }

    // Local object URL fallback
    const localUrl = URL.createObjectURL(file);
    return {
      id: 'med_' + Math.random().toString(36).slice(2, 10),
      filename: file.name,
      content_type: file.type,
      url: localUrl,
      size_bytes: file.size,
    };
  },

  // Auth
  async getMe(): Promise<{ user: User | null; jobs?: any[] }> {
    try {
      const res = await fetch('/api/auth/me');
      if (res.ok) return await res.json();
    } catch (e) {
      console.warn('Auth offline check', e);
    }
    const stored = localStorage.getItem('wtw_auth_user');
    return { user: stored ? JSON.parse(stored) : null };
  },

  async loginWithGoogle(credential: string): Promise<User> {
    const res = await fetch('/api/auth/google', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential }),
    });
    if (!res.ok) throw new Error('Google sign-in failed');
    const data = await res.json();
    localStorage.setItem('wtw_auth_user', JSON.stringify(data.user));
    return data.user;
  },

  async demoLogin(name = 'Dean (Master Grower)', email = 'dean@watchthis.work'): Promise<User> {
    const demoUser = {
      name,
      email,
      avatar_url: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80',
    };
    try {
      const res = await fetch('/api/auth/google', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ demoUser }),
      });
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('wtw_auth_user', JSON.stringify(data.user));
        return data.user;
      }
    } catch (e) {
      console.warn(e);
    }

    const localUser: User = {
      id: 'usr_demo_vip',
      name: demoUser.name,
      email: demoUser.email,
      avatar_url: demoUser.avatar_url,
      is_premium: true,
    };
    localStorage.setItem('wtw_auth_user', JSON.stringify(localUser));
    return localUser;
  },

  async togglePremium(isPremium: boolean): Promise<boolean> {
    try {
      const res = await fetch('/api/auth/premium', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_premium: isPremium }),
      });
      if (res.ok) {
        const data = await res.json();
        return data.is_premium;
      }
    } catch (e) {
      console.warn(e);
    }
    const current = localStorage.getItem('wtw_auth_user');
    if (current) {
      const u = JSON.parse(current);
      u.is_premium = isPremium;
      localStorage.setItem('wtw_auth_user', JSON.stringify(u));
    }
    return isPremium;
  },

  async logout(): Promise<void> {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch (e) {
      console.warn(e);
    }
    localStorage.removeItem('wtw_auth_user');
  },
};
